import React from 'react';

export function ScoreRing({ score, max = 100, size = 96, stroke = 8, label }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.min(Math.max(score / max, 0), 1);
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(31,29,26,0.08)" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E06E53" strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct)}
          style={{ transition: 'stroke-dashoffset .8s ease' }} />
      </svg>
      <div className="absolute text-center">
        <div className="font-heading font-extrabold text-2xl leading-none">{score}</div>
        {label && <div className="text-[10px] text-faint uppercase tracking-wider mt-0.5">{label}</div>}
      </div>
    </div>
  );
}

export function Spinner({ className = 'w-5 h-5' }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none">
      <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

export function SkeletonBlock({ className = '' }) {
  return <div className={`skeleton ${className}`} data-testid="skeleton-loader" />;
}

export function Badge({ children, color = 'ember' }) {
  const colors = {
    ember: 'bg-ember/15 text-ember border-ember/30',
    mint: 'bg-mint/15 text-mint border-mint/30',
    sub: 'bg-elevated text-sub border-line',
    warn: 'bg-butter/40 text-yellow-700 border-sun/50',
  };
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-md border ${colors[color] || colors.sub}`}>
      {children}
    </span>
  );
}

export function TrendChart({ data, height = 120 }) {
  // data: [{date, avg}] — avg out of 10
  if (!data || data.length === 0) {
    return <div className="text-faint text-sm py-8 text-center">Complete a voice session to see your trend.</div>;
  }
  const w = 320, h = height, pad = 14;
  const points = data.map((d, i) => {
    const x = data.length === 1 ? w / 2 : pad + (i * (w - 2 * pad)) / (data.length - 1);
    const y = h - pad - ((d.avg / 10) * (h - 2 * pad));
    return { x, y, ...d };
  });
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const area = `${path} L${points[points.length - 1].x},${h - pad} L${points[0].x},${h - pad} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" data-testid="trend-chart">
      <defs>
        <linearGradient id="emberFade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#E06E53" stopOpacity="0.30" />
          <stop offset="100%" stopColor="#E06E53" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[2.5, 5, 7.5].map((g) => (
        <line key={g} x1={pad} x2={w - pad} y1={h - pad - (g / 10) * (h - 2 * pad)} y2={h - pad - (g / 10) * (h - 2 * pad)}
          stroke="rgba(31,29,26,0.08)" strokeDasharray="4 4" />
      ))}
      <path d={area} fill="url(#emberFade)" />
      <path d={path} fill="none" stroke="#E06E53" strokeWidth="2.5" strokeLinecap="round" />
      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="3.5" fill="#FDFBF7" stroke="#E06E53" strokeWidth="2" />
      ))}
    </svg>
  );
}

export function CopyButton({ text, testId }) {
  const [copied, setCopied] = React.useState(false);
  return (
    <button
      data-testid={testId || 'copy-button'}
      onClick={async () => {
        try { await navigator.clipboard.writeText(text); } catch (e) {
          const ta = document.createElement('textarea');
          ta.value = text; document.body.appendChild(ta); ta.select();
          document.execCommand('copy'); document.body.removeChild(ta);
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
      }}
      className="btn-ghost !min-h-[36px] !px-3 text-xs"
    >
      {copied ? 'Copied ✓' : 'Copy'}
    </button>
  );
}

export function ErrorNote({ children }) {
  if (!children) return null;
  return <div className="text-red-500 text-sm bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3" data-testid="error-message">{children}</div>;
}
